import React, { useContext, useState } from "react";
import { Link } from "react-router-dom"
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Paper,
  CircularProgress,
} from "@material-ui/core";
import _ from "lodash";
import { PageErrorContent } from "../page-error/PageError";
import { PAGE_ERROR } from "../../constants/MediaSearch";
import { ContentFinderContext } from "../../context/ContentFinderContext";
import { SERIES_TYPES } from "../../constants/ContentFinder";
import "../../styles/contentFinder.scss";
import "../../styles/media-search.scss";

const headCells = [
  {
    id: "TitleId",
    label: "Title ID",
    width: "10%",
  },
  {
    id: "TitleName",
    label: "Title Name",
    isLink: true,
    path: {
      series: "/legacyGmsc/series/",
      other: "/legacyGmsc/feature/",
    },
    width: "22%",
  },
  {
    id: "ReleaseYear",
    label: "Year",
    width: "8%",
  },
  {
    id: "TitleType",
    label: "Type",
    width: "10%",
  },
  {
    id: "Language",
    label: "Language",
    width: "12%",
  },
  {
    id: "SeriesTitleName",
    label: "Series",
    width: "22%",
  },
  {
    id: "SeasonNumber",
    label: "Season",
    width: "8%",
  },
  {
    id: "SeasonEpisodeNumber",
    label: "Ep #",
    width: "8%",
  },
];

const getValue = (item, key) => {
  switch (key) {
    case "TitleType":
      return _.get(item, "TitleType.Name", "-");

    case "Language":
      return _.startCase(_.toLower(_.get(item, "Language.Name", "-")));

    default:
      return _.get(item, key, "-");
  }
};

const getTitlePath = (item, path) => {
  const titleType = _.toLower(_.get(item, "TitleType.Name", ""));
  const isSeries = _.includes(SERIES_TYPES, titleType);
  const titleId = _.get(item, "TitleId", "");
  return isSeries ? `${path.series}${titleId}` : `${path.other}${titleId}`;
};

const rowsPerPageOptions = [10, 25, 50, 100];

export default function ContentFinderSearchGrid({
  results = [],
  loading = false,
  hasSearched = false,
  pageError = false,
}) {
  const { fetchTitleDetails } = useContext(ContentFinderContext);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedResults = results.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <div className="contentFinderSearchGrid">
        { loading ? (
          <div className="media-asset content-finder-loading">
            <CircularProgress size={35} />
          </div>
        ) : !loading && pageError ? (
          <div className="hbo-promo">
            <div className="hbo-promo-error">
              <PageErrorContent
                title={PAGE_ERROR.title}
                message={PAGE_ERROR.message}
                reloadBtn={false}
              />
            </div>
          </div>
        ) : hasSearched && _.isEmpty(results) ? (
          <div className="tableInfoDiv">
            <span className="tableInfoSpan">
              No Titles found
            </span>
          </div>
        )
        : !hasSearched && _.isEmpty(results) ? null : (
          <>
            <div className="contentFinderTableInfo">
              <span className="contentFinderTableInfoSpan">
                {results.length > 1 ? `${results.length} titles found` : `${results.length} title found`}
              </span>
            </div>
            <TableContainer
              component={Paper}
              className="content-finder-table-container"
            >
              <Table
                aria-label="content finder results table"
                className="content-finder-table"
              >
                <TableHead className="tableHeader content-finder-table-head">
                  <TableRow className="tableRow content-finder-header-row">
                    {headCells.map((column, index) => (
                      <TableCell
                        key={`header_${index}`}
                        className="media-cell"
                        style={{ width: column.width }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody id="table_container" className="content-finder-table-body">
                  {paginatedResults.map((item, index) => (
                    <TableRow
                      className="tableRow content-finder-body-row"
                      key={item.TitleId || index}
                    >
                      {headCells.map((cell) => (  
                        <TableCell
                          key={cell.id}
                          className="content-finder-table-cell"
                          style={{ width: cell.width }}
                        >
                          {cell.isLink ? (
                            <Link
                              to={getTitlePath(item, cell.path)}
                              onClick={() =>
                                fetchTitleDetails(
                                  _.get(item, "TitleId", ""),
                                  _.get(item, "TitleType.Name", "")
                                )
                              }
                              className="contentFinderTableCellLink"
                            >
                              {getValue(item, cell.id)}
                            </Link>
                          ) : (
                            getValue(item, cell.id)
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </div>

      {!loading && results?.length > 10 && (
        <TablePagination
          component="div"
          className="tablePagination"
          classes={{
              toolbar: "tablePaginationToolbar",
          }}
          count={results.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          nextIconButtonText="Next Page"
          backIconButtonText="Previous Page"
        />
      )}
    </>
  );
}